import { createFileRoute } from "@tanstack/react-router";
import { Section, EyebrowHeading } from "@/components/site/Section";
import { LeadMagnetForm } from "@/components/site/LeadMagnetForm";
import { breadcrumbs, creativeWork, faqPage, aeoGeo } from "@/lib/seo/schema";
import {
  CheckCircle2,
  FileText,
  Clock,
  Target,
  Megaphone,
  Film,
  Users,
} from "lucide-react";

const FAQ = [
  {
    q: "Vad innehåller Formatguide 2026?",
    a: "24 sidor med konkreta rekommendationer: vilka filmformat som funkar var (feed, story, YouTube, webinar, paid social), rätt längder per kanal, hook-mönster som håller kvar tittaren och en rytm-mall för månadsproduktion.",
  },
  {
    q: "Kostar guiden något?",
    a: "Nej. Formatguiden är helt gratis. Du skriver in din mejl och får PDF:en direkt — ingen säljkö, inga kostnader.",
  },
  {
    q: "Vad händer med min mejladress?",
    a: "Vi sparar den för att kunna skicka guiden och eventuellt en uppföljning. Vi spammar inte och säljer aldrig vidare din adress. Du kan be oss radera den när som helst.",
  },
  {
    q: "Vem är guiden för?",
    a: "Marknadsteam, grundare, kommunikatörer och SoMe-ansvariga som vill producera film med struktur istället för gissningar — oavsett om ni gör allt själva eller jobbar med byrå.",
  },
];

const INSIDE = [
  {
    icon: Target,
    title: "Format per kanal",
    body: "Vilket format som faktiskt presterar på feed, story, YouTube, webinar och paid social — och varför.",
  },
  {
    icon: Clock,
    title: "Rätt längder",
    body: "Konkreta längdrekommendationer per kanal och syfte, från 9-sekunders paid till 90-sekunders LinkedIn.",
  },
  {
    icon: Megaphone,
    title: "Hook-mönster",
    body: "Beprövade öppningar som håller kvar tittaren de avgörande första sekunderna.",
  },
  {
    icon: Film,
    title: "Rytm-mall",
    body: "En enkel mall för att planera månadsproduktion så att en inspelning blir flera färdiga klipp.",
  },
];

const FOR_WHOM = [
  "Marknadsteam som vill ha struktur på sin filmproduktion",
  "Grundare som gör mycket själva och vill prioritera rätt",
  "Kommunikatörer som ska motivera format internt",
  "SoMe-ansvariga som producerar i hög takt",
];

export const Route = createFileRoute("/formatguide-2026")({
  head: () => ({
    meta: [
      { title: "Formatguide 2026 — gratis PDF om filmformat | Fenny" },
      {
        name: "description",
        content:
          "Ladda ner Fennys Formatguide 2026 gratis. 24 sidor om vilket filmformat som funkar var, rätt längder, hooks och en rytm-mall för månadsproduktion.",
      },
      { property: "og:title", content: "Formatguide 2026 — gratis PDF | Fenny" },
      {
        property: "og:description",
        content:
          "Vilket filmformat funkar var? En lathund för feed, story, YouTube, webinar och paid social — format, längder, hooks och rytm-mall.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/formatguide-2026" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/formatguide-2026" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/formatguide-2026", name: "Formatguide 2026 — gratis PDF om filmformat | Fenny", description: "Ladda ner Fennys Formatguide 2026 gratis. 24 sidor om vilket filmformat som funkar var, rätt längder, hooks och en rytm-mall för månadsproduktion."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          creativeWork({
            name: "Formatguide 2026",
            path: "/formatguide-2026",
            description:
              "Gratis PDF-guide om filmformat: vilket format som funkar var, rätt längder per kanal, hook-mönster och en rytm-mall för månadsproduktion.",
            datePublished: "2026-01-01",
            about: ["filmformat", "videomarknadsföring", "innehållsproduktion"],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Guider", path: "/guider" },
            { name: "Formatguide 2026", path: "/formatguide-2026" },
          ]),
        ),
      },
    ],
  }),
  component: FormatguidePage,
});

function FormatguidePage() {
  return (
    <article>
      {/* Hero with form */}
      <Section className="pb-12 pt-16 md:pb-16 md:pt-24">
        <div className="grid items-start gap-12 lg:grid-cols-2">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-mint/30 bg-mint/10 px-3 py-1 text-xs uppercase tracking-[0.18em] text-mint">
              <FileText className="h-3.5 w-3.5" /> Gratis PDF · 24 sidor
            </div>
            <h1 className="mt-6 text-balance text-4xl font-semibold leading-[1.05] md:text-6xl">
              Formatguide 2026 — vilket filmformat passar er?
            </h1>
            <p className="mt-6 max-w-xl text-pretty text-base text-muted-foreground md:text-lg">
              Sluta gissa. Den här guiden visar vilket format som funkar var, rätt
              längder per kanal, hooks som håller kvar tittaren och en rytm-mall
              för att producera film månad efter månad — utan att uppfinna hjulet
              varje gång.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Konkreta format- och längdrekommendationer per kanal",
                "Hook-mönster som funkar i feed och paid",
                "Rytm-mall för månadsproduktion",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-foreground/85">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-mint" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:sticky lg:top-24">
            <LeadMagnetForm location="formatguide_landing" variant="section" id="ladda-ner" />
          </div>
        </div>
      </Section>

      {/* What's inside */}
      <Section className="py-12 md:py-16">
        <EyebrowHeading
          eyebrow="Vad du får"
          title={<>Det här finns i guiden</>}
          lede="Inga floskler — bara det vi själva använder för att planera film som presterar."
        />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {INSIDE.map((b) => (
            <article
              key={b.title}
              className="flex flex-col rounded-3xl border border-white/10 bg-surface p-7"
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-mint/15 text-mint ring-1 ring-mint/30">
                <b.icon className="h-5 w-5" strokeWidth={2} />
              </div>
              <h3 className="mt-5 text-lg font-semibold leading-tight">{b.title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{b.body}</p>
            </article>
          ))}
        </div>
      </Section>

      {/* Who it's for */}
      <Section className="py-12 md:py-16">
        <div className="rounded-3xl border border-white/10 bg-surface/60 p-8 md:p-12">
          <div className="grid items-center gap-8 md:grid-cols-[auto,1fr]">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-mint/15 text-mint ring-1 ring-mint/30">
              <Users className="h-7 w-7" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-balance text-2xl font-semibold md:text-3xl">
                Vem är guiden för?
              </h2>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {FOR_WHOM.map((w) => (
                  <div key={w} className="flex items-start gap-3 text-sm text-foreground/85">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-mint" />
                    {w}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section className="py-12 md:py-16">
        <EyebrowHeading eyebrow="Vanliga frågor" title={<>Bra att veta</>} />
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {FAQ.map((f) => (
            <div key={f.q} className="rounded-2xl border border-white/10 bg-surface/40 p-6">
              <div className="text-base font-semibold text-foreground">{f.q}</div>
              <p className="mt-3 text-pretty text-sm leading-relaxed text-foreground/85">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Closing CTA back to form */}
      <Section className="pb-20 pt-12">
        <div className="rounded-3xl border border-mint/30 bg-surface/60 p-8 text-center md:p-12">
          <h2 className="text-balance text-3xl font-semibold md:text-4xl">
            Hämta Formatguide 2026
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
            Skriv in din mejl så får du PDF:en direkt. Ingen säljkö, ingen spam.
          </p>
          <div className="mt-7 flex justify-center">
            <a
              href="#ladda-ner"
              className="inline-flex items-center gap-2 rounded-full bg-mint px-6 py-3 text-sm font-semibold text-background transition-opacity hover:opacity-90"
            >
              Ladda ner guiden →
            </a>
          </div>
        </div>
      </Section>
    </article>
  );
}
